import { FC, useEffect } from "react";

import {
  useAssessedCountData,
  useAssessmentMode,
  useAssessmentPublishActions,
} from "../../../../utils/assessment";

import { AssessmentStatus } from "api/api-data-types";
import { useIsAssessing } from "components/materials/section/assessment/hooks";
import { PageType } from "components/server-types";
import { t } from "i18n/i18n";
import type { ResourceKey } from "i18n/i18next";
import { Banner, BannerProps } from "mds/components/Banner";
import { LegacyButton } from "mds/components/LegacyButton";
import { AssessPointsMarkerIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { useAppDispatch, useAppSelector } from "store/index";
import {
  selectAssessmentsByPageId,
  selectOfficialFullSubmissionsByPageId,
  selectShouldShowAssessmentSummary,
  selectSubmissionsByPageId,
} from "store/selectors";
import { selectAssessmentInfoForPage } from "store/selectors/computed";
import { viewStateActions } from "store/slices/view";
import { useListSelector } from "store/store-hooks";
import { isGroupPage, isIndividualPage } from "utils/page-utils";

type BannerTextProps = {
  textKey: ResourceKey;
  count: number;
  total: number;
};

const BannerText: FC<BannerTextProps> = ({ textKey, count, total }) => t(textKey, { count, total });

type AssessmentBannerProps = {
  className?: string;
  page: PageType;
};

export const AssessmentBanner: FC<AssessmentBannerProps> = ({ className, page }) => {
  const dispatch = useAppDispatch();
  const submissions = useAppSelector((s) => selectSubmissionsByPageId(s, page.id));
  const assessments = useAppSelector((s) => selectAssessmentsByPageId(s, page.id));
  const assessmentInfo = useAppSelector((s) => selectAssessmentInfoForPage(s, page.id));
  const { canAuthorCourse } = useCapabilities();
  const isAssessing = useIsAssessing();
  const shouldShowAssessmentSummary = useAppSelector(selectShouldShowAssessmentSummary);

  const { publishGrades } = useAssessmentPublishActions(page);
  const { count: officialSubmissionsCount, total: totalSubmissionsCount } =
    useAssessedCountData(page);
  const {
    stopAssessing,
    startAssessing: onAssess,
    onExitAssessmentModeWithModal,
  } = useAssessmentMode(page);

  const officialSubmissions = useListSelector((s) =>
    selectOfficialFullSubmissionsByPageId(s, page?.id),
  );

  const isAllAssessed = officialSubmissions.length === totalSubmissionsCount;

  const defaultBannerProps: BannerProps = {
    className: "page-block-width text-sm font-medium text-black-tint-10",
    Icon: page.assessments_published_at ? undefined : AssessPointsMarkerIcon,
    kind: "success",
    children: "",
  };
  const context = isGroupPage(page) ? "group" : "individual";

  const showAssessmentBanner =
    assessmentInfo?.status !== AssessmentStatus.NOT_ASSESSABLE &&
    canAuthorCourse &&
    submissions.length !== 0 &&
    (isGroupPage(page) || isIndividualPage(page));

  // Banners that are shown when out of assessment mode
  const showSubmissionCollectedBanner =
    !isAssessing && !page.assessments_published_at && assessments.length === 0;
  const showAssessmentInProgressBanner =
    !isAssessing && !page.assessments_published_at && assessments.length !== 0;
  const showEditAssessmentBanner =
    !isAssessing && page.assessments_published_at && shouldShowAssessmentSummary;
  const showViewAssessmentsBanner =
    !isAssessing && page.assessments_published_at && !shouldShowAssessmentSummary;

  // Banners that are shown in assessment mode
  const showAssessmentProgressBanner = isAssessing && !page.assessments_published_at;
  const showAssessmentPublishedBanner = isAssessing && page.assessments_published_at;

  const onExitAssessmentMode = onExitAssessmentModeWithModal(
    officialSubmissionsCount,
    totalSubmissionsCount,
  );

  const onPublishGrades = () => {
    publishGrades();
    stopAssessing();
  };

  useEffect(() => {
    dispatch(viewStateActions.setShowAssessmentSummary(false));
  }, [page.id, dispatch]);

  if (!showAssessmentBanner) return null;

  if (showSubmissionCollectedBanner) {
    return (
      <div className={className}>
        <Banner
          {...defaultBannerProps}
          actionButtonSize="xs"
          actionButtonText={t("assessment.assess")}
          eventName="Assessment - Start"
          eventProperties={{ pageTitle: page.title, pageId: page.id }}
          kind="info"
          onAction={onAssess}
        >
          {t("assessment.submissions_collected", { context, count: totalSubmissionsCount })}
        </Banner>
      </div>
    );
  }

  if (showAssessmentInProgressBanner) {
    return (
      <div className={className}>
        <Banner
          {...defaultBannerProps}
          actionButtonSize="xs"
          actionButtonText={t("assessment.resume_assessment")}
          eventName="Assessment - Start"
          eventProperties={{ pageTitle: page.title, pageId: page.id }}
          onAction={onAssess}
        >
          <BannerText
            count={officialSubmissionsCount}
            textKey="assessment.assessment_in_progress"
            total={totalSubmissionsCount}
          />
        </Banner>
      </div>
    );
  }

  if (showAssessmentProgressBanner) {
    return (
      <div className={className}>
        <Banner
          {...defaultBannerProps}
          buttonChildren={
            <>
              <LegacyButton
                className="border !border-green"
                eventName="Assessment - Stop"
                eventProperties={{ pageId: page.id, pageTitle: page.title }}
                kind="secondary"
                size="xs"
                onClick={onExitAssessmentMode}
              >
                {t("assessment.finish_later")}
              </LegacyButton>

              <LegacyButton
                className={
                  assessments.length === 0
                    ? "border !border-black-tint-93 !bg-black-tint-97"
                    : "border !border-green"
                }
                disabled={assessments.length === 0}
                eventName="Assessment - Publish"
                eventProperties={{ pageTitle: page.title, pageId: page.id }}
                kind="secondary"
                size="xs"
                onClick={onPublishGrades}
              >
                {t("assessment.publish_grades")}
              </LegacyButton>
            </>
          }
        >
          <BannerText
            count={officialSubmissionsCount}
            textKey="assessment.assessment_progress"
            total={totalSubmissionsCount}
          />
        </Banner>
      </div>
    );
  }

  if (showAssessmentPublishedBanner) {
    return (
      <div className={className}>
        <Banner
          {...defaultBannerProps}
          actionButtonClassName="border !border-green"
          actionButtonKind="secondary"
          actionButtonSize="xs"
          actionButtonText={t("assessment.stop_editing")}
          eventName="Assessment - Stop"
          eventProperties={{ pageTitle: page.title, pageId: page.id }}
          onAction={onExitAssessmentMode}
        >
          <BannerText
            count={officialSubmissionsCount}
            textKey="assessment.assessments_published"
            total={totalSubmissionsCount}
          />
        </Banner>
      </div>
    );
  }

  if (showViewAssessmentsBanner) {
    return (
      <div className={className}>
        <Banner
          {...defaultBannerProps}
          actionButtonClassName="border !border-green"
          actionButtonKind="secondary"
          actionButtonSize="xs"
          actionButtonText={t("assessment.view_assessments")}
          eventName="Assessment - View summary"
          eventProperties={{ pageTitle: page.title, pageId: page.id }}
          onAction={() => dispatch(viewStateActions.setShowAssessmentSummary(true))}
        >
          <BannerText
            count={officialSubmissionsCount}
            textKey="assessment.assessments_published"
            total={totalSubmissionsCount}
          />
        </Banner>
      </div>
    );
  }

  if (showEditAssessmentBanner) {
    return (
      <div className={className}>
        <Banner
          {...defaultBannerProps}
          actionButtonClassName="border !border-green"
          actionButtonKind="secondary"
          actionButtonSize="xs"
          actionButtonText={
            isAllAssessed ? t("assessment.edit_assessments") : t("assessment.resume_assessment")
          }
          eventName="Assessment - Edit"
          eventProperties={{ pageTitle: page.title, pageId: page.id }}
          onAction={onAssess}
        >
          <BannerText
            count={officialSubmissionsCount}
            textKey="assessment.assessments_published"
            total={totalSubmissionsCount}
          />
        </Banner>
      </div>
    );
  }

  return null;
};
